import { EventType, MoveEvent } from "@fatbees/schemas";
import { useQuantizedNow } from "@fatbees/ui-utils";
import { eq, lte } from "@tanstack/db";
import { isNull, useLiveQuery } from "@tanstack/react-db";
import { indexBy, prop } from "ramda";
import { useEffect } from "react";
import { useCollectionReady } from "./useCollectionReady";
import { EventsCollection } from "./useEventsCollectionInstance";
import { LoadsCollection } from "./useLoadsCollectionInstance";

export function useUpdateLoadLocations(
  eventsCollection: EventsCollection,
  loadsCollection: LoadsCollection,
) {
  const quantizedNow = useQuantizedNow();
  const ready = useCollectionReady(loadsCollection);

  const loadMoveEvents = useLiveQuery(
    (q) =>
      q
        .from({ event: eventsCollection })
        .where(({ event }) => eq(event.type, EventType.move))
        .where(({ event }) => isNull(event.hiveId))
        .where(({ event }) => lte(event.start, quantizedNow))
        .orderBy(({ event }) => event.start),
    [quantizedNow],
  ).data as (MoveEvent & { loadId: string })[];

  useEffect(() => {
    if (!ready) {
      return;
    }

    const eventByLoadId = indexBy(prop("loadId"), loadMoveEvents);

    loadsCollection.utils.writeBatch(() => {
      for (const load of loadsCollection.values()) {
        // values() includes optimistic rows, but writeUpdate validates
        // against the synced store only — updating a load whose insert is
        // still in flight throws UpdateOperationItemNotFoundError and
        // crashes the tree. Skip it; its location is recomputed on a later
        // effect run (at the latest, useQuantizedNow's next minute tick)
        // once it has synced.
        if (!loadsCollection._state.syncedData.has(load.id)) {
          continue;
        }

        const location = eventByLoadId[load.id] && {
          since: eventByLoadId[load.id].start,
          name: eventByLoadId[load.id].details.movedTo,
        };

        if (JSON.stringify(location) !== JSON.stringify(load.location)) {
          loadsCollection.utils.writeUpdate({ id: load.id, location });
        }
      }
    });
  }, [loadMoveEvents, ready, loadsCollection]);
}
